import styled, { CreateStyled } from '@emotion/styled'

import { useTheme } from 'emotion-theming'

import SColors from './colors'
import LogoWide from '@svgs/logo-wide.svg'
import RoutingService from '../utils/routing'
import { LocalStorageKeys } from 'utils/localStorage'

const standardOpinionless = {
    buttonBg: SColors.Orange350,
    buttonText: '#FFFFFF',
    dropdownBg: '#17191C',
    spinner: '#FFFFFF',
    divider: 'rgba(183, 184, 185, 0.1)',
    inputBg: '#FFFFFF',
    inputBorder: '#CCCCCC',
    inputPlaceholder: '#BBBCBC',
    inputError: '#FE1149',
    inputWarning: '#CC2936',
    inputLabel: SColors.Black,
    inputShadow: 'None',
    segmentedControlActiveText: '#FEFEFE',
    segmentedControlInactiveText: '#4A5266',
    segmentedControlTextHover: '#C0C0C0',
    segmentedControlBottomBorder: '#17191C',
    segmentedControlPaddle: '#FEFEFE',
    tabBarBg: '#17191C',
    tabBarActiveText: '#FEFEFE',
    tabBarInactiveText: '#C0C0C0',
}

const opinionlessComponentColors =
    // TO DO: Replace with hashmap
    typeof window !== 'undefined' &&
    window.location.href.includes('lbstaging') &&
    window.location.href.includes('longbeachpledge')
        ? { ...standardOpinionless, buttonBg: SColors.Blue350 }
        : typeof window !== 'undefined' && window.location.href.includes('chicago')
        ? {
              ...standardOpinionless,
              buttonBg: SColors.Orange350,
          }
        : {
              ...standardOpinionless,
              buttonBg: SColors.Blue350,
          }

const STheme = {
    breakpoints: [
        '900px', // 0 mobile
        '968px', // 1 tablet
        '1320px', // 2 desktop
        '414px', // 3 small screens
    ],
    fontSizes: [
        8, // 0
        10, // 1
        12, // 2
        14, // 3
        16, // 4
        32, // 5
        42, // 6
    ],
    logos: {
        mainLogo: '/f4gi-logo.png',
        policyEngineLogo: 'https://storage.googleapis.com/portal-web-resources/policy_engine_high_res.png',
        mobileLogo: LogoWide,
    },
    images: {
        sideBarImage: '/sidebar.jpg',
    },
    colors: {
        black: SColors.Black,
        blackLabelfont: SColors.BlackLabelFont,
        blackLabelFontAlpha: SColors.BlackLabelFontAlpha,
        white: SColors.White,
        primaryColor: SColors.Blue350,
        secondaryColor: SColors.Blue350,
        tertiaryColor: SColors.Blue350,
        hoverColor: SColors.Blue300,
        backgroundColor: SColors.Blue200,
        redError: SColors.RedError,
        purple50: SColors.Purple50,
        grayUnselectedRadioOutline: SColors.GrayUnselectedRadioOutline,
        grayUnselectedCheckboxBackground: SColors.GrayUnselectedCheckboxBackground,
        grayUnselectedCheckboxOutline: SColors.GrayUnselectedCheckboxOutline,
        grayLabelPlaceholder: SColors.GrayLabelPlaceholder,
        gray400: SColors.Gray400,
        gray350: SColors.Gray350,
        gray300: SColors.Gray300,
        gray200: SColors.Gray200,
        gray150: SColors.Gray150,
        gray125: SColors.Gray125,
        gray100: SColors.Gray100,
        gray50: SColors.Gray50,
        textColor: null,
        languageSwitcherActiveColor: null,
        languageSwitcherInactiveColor: null,
        darkGreen: SColors.DarkGreen,
        yellow: SColors.Yellow,
        lightGray: SColors.lightGray,
        adminBlue: SColors.Blue350,
        adminLightBlue: SColors.Blue300,
        adminLightestDisabled: SColors.Blue200,
        languageSwitcherLBDisabled: SColors.languageSwitcherLongBeachDisabled,
        lbTitleColor: SColors.lbGray,
        lbBlue: SColors.lbBlue,
        ...opinionlessComponentColors,
    },
    fonts: {
        default: 'Nunito Sans',
        header: 'Nunito Sans',
        terms: 'IBM Plex Sans',
        chicagoFTX: { header: 'IBM Plex Sans', body: 'IBM Plex Sans' },
        sonoma: { header: 'IBM Plex Sans', body: 'IBM Plex Sans' },
        longBeach: { header: 'Raleway', body: 'IBM Plex Sans' },
    },
    fontsV2: {
        default: 'IBM Plex Sans',
        'City of Long Beach': { header: 'Raleway', body: 'IBM Plex Sans' },
        'Pathway to Income Equity': { header: 'IBM Plex Sans', body: 'IBM Plex Sans' },
        'First 5 Sonoma County': { header: 'IBM Plex Sans', body: 'IBM Plex Sans' },
        'Equity and Transformation and FTX Foundation': { header: 'IBM Plex Sans', body: 'IBM Plex Sans' },
    },
    fontWeights: {
        extraLight: 200,
        light: 300,
        regular: 400,
        medium: 500,
        semiBold: 600,
        bold: 700,
        extraBold: 900,
    },
    radii: [
        4, // 0
        6, // 1
        8, // 2
        12, // 3
    ],
    space: [
        0, // 0
        2, // 1
        4, // 2
        8, // 3
        12, // 4
        16, // 5
        24, // 6
        32, // 7
        40, // 8
        48, // 9
        64, // 10
        96, // 11
        128, // 12
        256, // 13
    ],
    shadows: {
        dropdown: '-2px -2px 5px rgba(27, 30, 35, 0.5), 2px 2px 5px rgba(27, 30, 35, 0.5)',
        main: '0px 0px 20px rgba(0, 0, 0, 0.02)',
        languageSwitcher: '0px 0px 20px rgba(0, 0, 0, 0.2)',
        modal: '0px 0px 15px rgba(0, 0, 0, 0.2)',
    },
    buttons: {
        primary: {
            p: 0,
            m: 0,
            border: 'none',
            outline: 'none',
            background: 'none',
            cursor: 'pointer',
        },
    },
}

if (typeof window !== 'undefined') {
    RoutingService.pathChanged(window.location.pathname)
    const url = localStorage.getItem(LocalStorageKeys.GRAPHQL_ENDPOINT) || process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT
    let programFragment = RoutingService.getFragment(0)
    programFragment = programFragment === 'v2' ? RoutingService.getFragment(1) : programFragment

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'F4GI-Program': programFragment,
        },
        body: JSON.stringify({
            query: `
                    query Styles {
                        styles {
                            primaryColor
                            secondaryColor
                            tertiaryColor
                            hoverColor
                            backgroundColor
                            sidebarLogo
                            sidebarLogoMobile
                            sidebarImage
                            textColor
                            languageSwitcherActiveColor
                            languageSwitcherInactiveColor
                        }
                    }
                `,
            variables: null,
        }),
    }).then((response) => {
        return response.json().then((body) => {
            STheme.images.sideBarImage = body?.data?.styles?.sidebarImage
            STheme.colors.primaryColor = body?.data?.styles?.primaryColor
            STheme.colors.secondaryColor = body?.data?.styles?.secondaryColor
            STheme.colors.tertiaryColor = body?.data?.styles?.tertiaryColor
            STheme.colors.hoverColor = body?.data?.styles?.hoverColor
            STheme.colors.backgroundColor = body?.data?.styles?.backgroundColor
            STheme.colors.purple50 = body?.data?.styles?.senary
            STheme.logos.mainLogo = body?.data?.styles?.sidebarLogo
            STheme.logos.mobileLogo = body?.data?.styles?.sidebarLogoMobile
            STheme.colors.buttonBg = body?.data?.styles?.secondaryColor
            STheme.colors.textColor = body?.data?.styles?.textColor || null
            STheme.colors.languageSwitcherActiveColor = body?.data?.styles?.languageSwitcherActiveColor || null
            STheme.colors.languageSwitcherInactiveColor = body?.data?.styles?.languageSwitcherInactiveColor || null
        })
    })
}

if (typeof window !== 'undefined') {
    RoutingService.pathChanged(window.location.pathname)
    const url = localStorage.getItem(LocalStorageKeys.GRAPHQL_ENDPOINT) || process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT
    let programFragment = RoutingService.getFragment(0)
    programFragment = programFragment === 'v2' ? RoutingService.getFragment(1) : programFragment

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'F4GI-Program': programFragment,
        },
        body: JSON.stringify({
            query: `
                    query Organization {
                        organization {
                            version
                        }
                    }
                `,
            variables: null,
        }),
    }).then((response) => {
        return response.json().then((body) => {
            STheme.fonts.default = body.data.organization.version == 2 ? 'IBM Plex Sans' : 'Nunito Sans'
            STheme.fonts.header = body.data.organization.version == 2 ? 'IBM Plex Mono' : 'Nunito Sans'
        })
    })
}

export const SStyled = styled as CreateStyled<typeof STheme>
export const useSTheme = () => useTheme<typeof STheme>()

export default STheme
